(() => {
  const SELECTOR = '[data-buddy-benefit-slider]';
  const AUTOPLAY_VISIBILITY_THRESHOLD = 0.5;
  const namespace = window.BuddyBenefitSlider || {};

  if (namespace.loaded) {
    namespace.initAll?.();
    return;
  }

  namespace.loaded = true;
  namespace.instances = namespace.instances || new WeakMap();
  window.BuddyBenefitSlider = namespace;

  const instances = namespace.instances;

  class BuddyBenefitSlider {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-buddy-benefit-viewport]');
      this.track = section.querySelector('[data-buddy-benefit-track]');
      this.slides = Array.from(section.querySelectorAll('[data-buddy-benefit-slide]'));
      this.dots = Array.from(section.querySelectorAll('[data-buddy-benefit-dot]'));
      this.prevButton = section.querySelector('[data-buddy-benefit-prev]');
      this.nextButton = section.querySelector('[data-buddy-benefit-next]');
      this.videoButtons = Array.from(section.querySelectorAll('[data-buddy-benefit-video-toggle]'));
      this.index = 0;
      this.timer = null;
      this.restartTimer = null;
      this.scrollFrame = null;
      this.scrollEndTimer = null;
      this.resizeFrame = null;
      this.userInteractedAt = 0;
      this.canScroll = false;
      this.isHovering = false;
      this.isFocusWithin = false;
      this.requestedIndex = null;
      this.requestedScrollLeft = null;
      this.visibleVideos = new Set();
      this.videoObserver = null;
      this.isAutoplayVisible = !('IntersectionObserver' in window);
      this.autoplayObserver = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isMotionPaused = this.reducedMotion.matches;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      if (!this.viewport || !this.track || !this.slides.length) return;

      this.prepareMedia();
      this.bind();
      this.observeVideos();
      this.updateScrollability();
      this.update(0, true);
      this.startAutoplay();
      section.dataset.buddyBenefitInitialized = 'true';
    }

    bind() {
      this.prevButton?.addEventListener('click', () => this.userNavigate(this.index - 1), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.userNavigate(this.index + 1), { signal: this.signal });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.userNavigate(Number(dot.dataset.buddyBenefitDot || 0)), { signal: this.signal });
      });

      this.videoButtons.forEach((button) => {
        const video = button.closest('.buddy-benefit-slider__media-frame')?.querySelector('video');
        if (!video) return;

        button.addEventListener('click', () => this.toggleVideo(video, button), { signal: this.signal });
        video.addEventListener('play', () => this.updateVideoButton(button, false), { signal: this.signal });
        video.addEventListener('pause', () => this.updateVideoButton(button, true), { signal: this.signal });
        this.updateVideoButton(button, this.isMotionPaused);
      });

      this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.signal });
      this.viewport.addEventListener('pointerdown', () => this.pauseForInteraction(), { signal: this.signal });
      this.viewport.addEventListener('touchstart', () => this.pauseForInteraction(), { passive: true, signal: this.signal });
      this.viewport.addEventListener('wheel', () => this.pauseForInteraction(), { passive: true, signal: this.signal });
      this.viewport.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      window.addEventListener('resize', () => this.onResize(), { signal: this.signal });

      if (this.section.dataset.pauseOnHover === 'true') {
        this.section.addEventListener('mouseenter', () => {
          this.isHovering = true;
          this.stopAutoplay();
        }, { signal: this.signal });
        this.section.addEventListener('mouseleave', () => {
          this.isHovering = false;
          this.startAutoplay();
        }, { signal: this.signal });
        this.section.addEventListener('focusin', () => {
          this.isFocusWithin = true;
          this.stopAutoplay();
        }, { signal: this.signal });
        this.section.addEventListener('focusout', () => {
          this.isFocusWithin = false;
          this.startAutoplay();
        }, { signal: this.signal });
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
          this.pauseAllVideos();
        } else {
          this.syncMedia();
          this.startAutoplay();
        }
      }, { signal: this.signal });

      if ('IntersectionObserver' in window) {
        this.autoplayObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const wasAutoplayVisible = this.isAutoplayVisible;
            this.isAutoplayVisible = entry.isIntersecting && entry.intersectionRatio >= AUTOPLAY_VISIBILITY_THRESHOLD;

            if (this.isAutoplayVisible && !wasAutoplayVisible) {
              this.startAutoplay();
            } else if (!this.isAutoplayVisible && wasAutoplayVisible) {
              this.stopAutoplay();
            }
          });
        }, { threshold: [0, AUTOPLAY_VISIBILITY_THRESHOLD] });
        this.autoplayObserver.observe(this.section);
      }

      this.reducedMotion.addEventListener?.('change', () => {
        if (this.reducedMotion.matches) {
          this.isMotionPaused = true;
          this.stopAutoplay();
          this.pauseAllVideos();
        } else {
          this.isMotionPaused = false;
          this.syncMedia();
          this.startAutoplay();
        }
      }, { signal: this.signal });
    }

    shouldAutoplayVideos() {
      return this.section.dataset.videoAutoplay !== 'false' && !this.isMotionPaused;
    }

    prepareMedia() {
      const shouldAutoplayVideos = this.shouldAutoplayVideos();

      this.slides.forEach((slide, index) => {
        slide.querySelectorAll('video').forEach((video) => {
          video.querySelector('img:not([alt])')?.setAttribute('alt', '');
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.setAttribute('playsinline', '');
          video.preload = index === 0 ? 'metadata' : 'none';
          if (!shouldAutoplayVideos || index !== 0) video.removeAttribute('autoplay');
          if (!shouldAutoplayVideos) video.controls = true;
        });
      });
    }

    observeVideos() {
      const videos = this.section.querySelectorAll('video');
      if (!videos.length) return;

      if (!('IntersectionObserver' in window)) {
        videos.forEach((video) => this.visibleVideos.add(video));
        this.syncMedia();
        return;
      }

      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            this.visibleVideos.add(video);
          } else {
            this.visibleVideos.delete(video);
          }
        });

        this.syncMedia();
      }, { threshold: [0, 0.35, 0.7] });

      videos.forEach((video) => this.videoObserver.observe(video));
    }

    onResize() {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        const wasScrollable = this.canScroll;
        this.updateScrollability();

        if (!this.canScroll) {
          this.viewport.scrollTo({ left: 0, behavior: 'auto' });
          this.update(0, true);
          return;
        }

        if (!wasScrollable) {
          this.goTo(this.index, 'auto');
        }
      });
    }

    updateScrollability() {
      this.canScroll = this.viewport.scrollWidth > this.viewport.clientWidth + 2;
      this.section.classList.toggle('buddy-benefit-slider--static', !this.canScroll);

      [this.prevButton, this.nextButton].forEach((button) => {
        if (!button) return;
        button.disabled = !this.canScroll;
        button.setAttribute('aria-disabled', this.canScroll ? 'false' : 'true');
      });

      this.dots.forEach((dot) => {
        dot.disabled = !this.canScroll;
        dot.setAttribute('aria-disabled', this.canScroll ? 'false' : 'true');
      });

      if (!this.canScroll) this.stopAutoplay();
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.userNavigate(this.index - 1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.userNavigate(this.index + 1);
      }
    }

    onScroll() {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => {
        if (this.requestedIndex !== null) {
          const atRequestedPosition = Math.abs(this.viewport.scrollLeft - this.requestedScrollLeft) < 3;
          if (atRequestedPosition) this.update(this.requestedIndex, false);
          window.clearTimeout(this.scrollEndTimer);
          this.scrollEndTimer = window.setTimeout(() => {
            this.requestedIndex = null;
            this.requestedScrollLeft = null;
          }, 450);
          return;
        }

        const nextIndex = this.getNearestIndex();
        if (nextIndex !== this.index) this.update(nextIndex, false);
      });
    }

    getNearestIndex() {
      const maxScrollLeft = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const scrollTolerance = 3;

      if (this.viewport.scrollLeft <= scrollTolerance) return 0;
      if (maxScrollLeft > 0 && this.viewport.scrollLeft >= maxScrollLeft - scrollTolerance) {
        return this.slides.length - 1;
      }

      const viewportRect = this.viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + (viewportRect.width / 2);
      let closestIndex = this.index;
      let closestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + (slideRect.width / 2);
        const distance = Math.abs(slideCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    normalizeIndex(index) {
      const lastIndex = this.slides.length - 1;
      if (index < 0) return lastIndex;
      if (index > lastIndex) return 0;
      return index;
    }

    userNavigate(index) {
      if (!this.canScroll) return;
      this.pauseForInteraction();
      this.goTo(index);
    }

    pauseForInteraction() {
      this.userInteractedAt = Date.now();
      this.requestedIndex = null;
      this.requestedScrollLeft = null;
      this.stopAutoplay();
      window.clearTimeout(this.restartTimer);
      this.restartTimer = window.setTimeout(() => this.startAutoplay(), Math.max(1800, this.getInterval()));
    }

    getTargetScrollLeft(slide) {
      const maxScrollLeft = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const targetScrollLeft = Math.max(0, slide.offsetLeft - this.track.offsetLeft);
      return Math.min(targetScrollLeft, maxScrollLeft);
    }

    goTo(index, behavior = 'smooth') {
      this.updateScrollability();
      if (!this.canScroll) {
        this.update(0, true);
        return;
      }
      if (!this.slides.length) return;
      const normalizedIndex = this.normalizeIndex(index);
      const slide = this.slides[normalizedIndex];
      if (!slide) return;

      const targetScrollLeft = this.getTargetScrollLeft(slide);
      this.requestedIndex = normalizedIndex;
      this.requestedScrollLeft = targetScrollLeft;
      this.viewport.scrollTo({ left: targetScrollLeft, behavior: this.reducedMotion.matches ? 'auto' : behavior });
      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        this.requestedIndex = null;
        this.requestedScrollLeft = null;
      }, 650);
      this.update(normalizedIndex, true);
    }

    update(index, syncMedia = true) {
      this.index = index;

      this.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (syncMedia) this.syncMedia();
    }

    playVideo(video) {
      if (video.dataset.buddyUserPaused === 'true') return;
      video.preload = 'metadata';
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    }

    updateVideoButton(button, isPaused) {
      button.setAttribute('aria-pressed', String(isPaused));
      button.setAttribute('aria-label', isPaused ? 'Play benefit video' : 'Pause benefit video');
      button.querySelector('[data-motion-pause-icon]')?.toggleAttribute('hidden', isPaused);
      button.querySelector('[data-motion-play-icon]')?.toggleAttribute('hidden', !isPaused);
    }

    toggleVideo(video, button) {
      const shouldPlay = video.paused || video.dataset.buddyUserPaused === 'true';

      if (shouldPlay) {
        delete video.dataset.buddyUserPaused;
        this.isMotionPaused = false;
        this.playVideo(video);
        this.updateVideoButton(button, false);
        this.startAutoplay();
      } else {
        video.dataset.buddyUserPaused = 'true';
        video.pause();
        this.updateVideoButton(button, true);
        this.stopAutoplay();
      }
    }

    pauseAllVideos() {
      this.section.querySelectorAll('video').forEach((video) => video.pause());
    }

    syncMedia() {
      if (!this.shouldAutoplayVideos()) return;

      if (this.isMotionPaused || this.reducedMotion.matches || document.hidden) {
        this.pauseAllVideos();
        return;
      }

      this.section.querySelectorAll('video').forEach((video) => {
        if (this.visibleVideos.has(video) && video.dataset.buddyUserPaused !== 'true') {
          this.playVideo(video);
        } else {
          video.pause();
        }
      });
    }

    getInterval() {
      return Number(this.section.dataset.autoplayInterval) || 5000;
    }

    shouldPauseForHoverOrFocus() {
      return this.section.dataset.pauseOnHover === 'true' && (this.isHovering || this.isFocusWithin);
    }

    getMaxScrollLeft() {
      return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    }

    getAutoplayNextIndex() {
      const maxScrollLeft = this.getMaxScrollLeft();
      const isAtRightEdge = maxScrollLeft > 0 && this.viewport.scrollLeft >= maxScrollLeft - 3;

      if (isAtRightEdge || this.index >= this.slides.length - 1) {
        return 0;
      }

      return this.index + 1;
    }

    isSectionMostlyVisible() {
      const rect = this.section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));

      return rect.height > 0 && visibleHeight / rect.height >= AUTOPLAY_VISIBILITY_THRESHOLD;
    }

    autoAdvance() {
      if (!this.isAutoplayVisible || document.hidden || !this.isSectionMostlyVisible()) {
        this.isAutoplayVisible = false;
        this.stopAutoplay();
        return;
      }

      this.goTo(this.getAutoplayNextIndex());
    }

    startAutoplay() {
      this.updateScrollability();
      if (!this.canScroll) return;
      if (this.section.dataset.autoplay !== 'true') return;
      if (this.isMotionPaused) return;
      if (Array.from(this.visibleVideos).some((video) => video.dataset.buddyUserPaused === 'true')) return;
      if (this.shouldPauseForHoverOrFocus()) return;
      if (!this.isAutoplayVisible) return;
      if (this.slides.length < 2 || this.reducedMotion.matches || document.hidden) return;
      if (Date.now() - this.userInteractedAt < 1200) return;

      this.stopAutoplay();
      this.timer = window.setInterval(() => this.autoAdvance(), this.getInterval());
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    destroy() {
      this.stopAutoplay();
      window.cancelAnimationFrame(this.scrollFrame);
      window.cancelAnimationFrame(this.resizeFrame);
      window.clearTimeout(this.scrollEndTimer);
      window.clearTimeout(this.restartTimer);
      this.videoObserver?.disconnect();
      this.autoplayObserver?.disconnect();
      this.abortController.abort();
      this.pauseAllVideos();
      this.section.removeAttribute('data-buddy-benefit-initialized');
    }
  }

  const initSection = (section) => {
    if (!section || instances.has(section) || section.dataset.buddyBenefitInitialized === 'true') return;
    const instance = new BuddyBenefitSlider(section);
    instances.set(section, instance);
  };

  const initAll = (container = document) => {
    container.querySelectorAll(SELECTOR).forEach(initSection);
  };

  const destroySection = (section) => {
    const target = section.matches?.(SELECTOR) ? section : section.querySelector?.(SELECTOR);
    const instance = target ? instances.get(target) : null;
    if (!instance) return;
    instance.destroy();
    instances.delete(target);
  };

  namespace.initAll = initAll;
  namespace.destroySection = destroySection;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroySection(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest(SELECTOR);
    if (!section) return;
    initSection(section);
    instances.get(section)?.goTo(Number(event.target.dataset.slideIndex || 0), 'auto');
  });
})();
